import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Switch } from '@/components/ui/switch';
import { Columns3, Save, Trash2, ChevronDown, Filter, X, Bookmark, ArrowUp, ArrowDown, ChevronsUpDown } from 'lucide-react';
import { TableHead } from '@/components/ui/table';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
function getAuthHeader() {
  return { Authorization: `Bearer ${localStorage.getItem('token')}` };
}

/**
 * Column definition:
 * { key: string, label: string, filterable?: boolean, filterType?: 'text'|'select', filterOptions?: string[] }
 */

export function useTableView({ pageKey, columns, data }) {
  const [columnFilters, setColumnFilters] = useState({});
  const [sortBy, setSortBy] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [visibleColumns, setVisibleColumns] = useState(() => 
    Object.fromEntries(columns.map(c => [c.key, true]))
  );
  const [savedViews, setSavedViews] = useState([]);
  const [activeViewId, setActiveViewId] = useState(null);

  useEffect(() => {
    fetchViews();
  }, [pageKey]);

  const fetchViews = async () => {
    try {
      const res = await axios.get(`${API}/saved-views?page_key=${pageKey}`, { headers: getAuthHeader() });
      setSavedViews(res.data);
    } catch { /* silent */ }
  };

  const saveView = async (name) => {
    const config = { columnFilters, visibleColumns, sortBy, sortDir };
    try {
      const res = await axios.post(`${API}/saved-views`, {
        page_key: pageKey, name, config
      }, { headers: getAuthHeader() });
      setSavedViews([...savedViews, res.data]);
      setActiveViewId(res.data.id);
    } catch { /* silent */ }
  };

  const loadView = (view) => {
    setColumnFilters(view.config.columnFilters || {});
    setVisibleColumns(view.config.visibleColumns || Object.fromEntries(columns.map(c => [c.key, true])));
    setSortBy(view.config.sortBy ?? null);
    setSortDir(view.config.sortDir || 'asc');
    setActiveViewId(view.id);
  };

  const deleteView = async (viewId) => {
    try {
      await axios.delete(`${API}/saved-views/${viewId}`, { headers: getAuthHeader() });
      setSavedViews(savedViews.filter(v => v.id !== viewId));
      if (activeViewId === viewId) setActiveViewId(null);
    } catch { /* silent */ }
  };

  // Cycle a column's sort: none -> asc -> desc -> none. `field` is the SERVER field name.
  const toggleSort = (field) => {
    setActiveViewId(null);
    if (sortBy !== field) { setSortBy(field); setSortDir('asc'); return; }
    if (sortDir === 'asc') { setSortDir('desc'); return; }
    setSortBy(null); setSortDir('asc');
  };

  const setColumnFilter = (key, value) => {
    setColumnFilters(prev => {
      if (!value) {
        const next = { ...prev };
        delete next[key];
        return next;
      }
      return { ...prev, [key]: value };
    });
    setActiveViewId(null);
  };

  const toggleColumn = (key) => {
    setVisibleColumns(prev => ({ ...prev, [key]: !prev[key] }));
    setActiveViewId(null);
  };

  const clearAllFilters = () => {
    setColumnFilters({});
    setActiveViewId(null);
  };

  const resetColumns = () => {
    setVisibleColumns(Object.fromEntries(columns.map(c => [c.key, true])));
    setActiveViewId(null);
  };

  // Filter data based on column filters
  const filteredData = data.filter(item => {
    for (const [key, filterValue] of Object.entries(columnFilters)) {
      if (!filterValue) continue;
      const col = columns.find(c => c.key === key);
      if (!col) continue;
      
      const cellValue = col.accessor ? col.accessor(item) : (item[key] ?? '');
      const strValue = String(cellValue).toLowerCase();
      
      if (col.filterType === 'select') {
        if (strValue !== filterValue.toLowerCase()) return false;
      } else {
        if (!strValue.includes(filterValue.toLowerCase())) return false;
      }
    }
    return true;
  });

  const activeFilterCount = Object.keys(columnFilters).length;
  const hiddenColumnCount = Object.values(visibleColumns).filter(v => !v).length;
  const activeViewName = savedViews.find(v => v.id === activeViewId)?.name;

  return {
    columnFilters, setColumnFilter, clearAllFilters,
    sortBy, sortDir, toggleSort,
    visibleColumns, toggleColumn, resetColumns,
    savedViews, saveView, loadView, deleteView,
    activeViewId, activeViewName,
    filteredData,
    activeFilterCount, hiddenColumnCount,
    isColumnVisible: (key) => visibleColumns[key] !== false
  };
}


export function TableViewToolbar({ 
  columns, columnFilters, setColumnFilter, clearAllFilters,
  visibleColumns, toggleColumn, resetColumns,
  savedViews, saveView, loadView, deleteView,
  activeViewName, activeFilterCount, hiddenColumnCount
}) {
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [viewName, setViewName] = useState('');

  const handleSave = () => {
    if (viewName.trim()) {
      saveView(viewName.trim());
      setViewName('');
      setSaveDialogOpen(false);
    }
  };

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="table-view-toolbar">
      {/* Saved Views Dropdown */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="h-8 text-xs gap-1" data-testid="views-dropdown">
            <Bookmark className="w-3.5 h-3.5" />
            {activeViewName || 'Views'}
            <ChevronDown className="w-3 h-3" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-56">
          {savedViews.length === 0 ? (
            <div className="px-2 py-3 text-xs text-slate-400 text-center">No saved views yet</div>
          ) : (
            savedViews.map(view => (
              <DropdownMenuItem key={view.id} className="flex items-center justify-between group" data-testid={`view-${view.id}`}>
                <span className="flex-1 cursor-pointer" onClick={() => loadView(view)}>{view.name}</span>
                <button 
                  onClick={(e) => { e.stopPropagation(); deleteView(view.id); }}
                  className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5 rounded hover:bg-red-50"
                >
                  <Trash2 className="w-3 h-3 text-red-400" />
                </button>
              </DropdownMenuItem>
            ))
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => setSaveDialogOpen(true)} data-testid="save-view-btn">
            <Save className="w-3.5 h-3.5 mr-2" />
            Save Current View
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Column Visibility */}
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className="h-8 text-xs gap-1" data-testid="columns-toggle">
            <Columns3 className="w-3.5 h-3.5" />
            Columns
            {hiddenColumnCount > 0 && (
              <Badge variant="secondary" className="ml-1 h-4 px-1 text-[10px]">{hiddenColumnCount} hidden</Badge>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-56 p-2">
          <div className="text-xs font-semibold text-slate-500 uppercase px-2 py-1 mb-1">Toggle Columns</div>
          <div className="space-y-1 max-h-64 overflow-auto">
            {columns.filter(c => c.key !== 'actions').map(col => (
              <label key={col.key} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-50 cursor-pointer" data-testid={`col-toggle-${col.key}`}>
                <Switch 
                  checked={visibleColumns[col.key] !== false} 
                  onCheckedChange={() => toggleColumn(col.key)}
                  className="scale-75"
                />
                <span className="text-sm text-slate-700">{col.label}</span>
              </label>
            ))}
          </div>
          {hiddenColumnCount > 0 && (
            <button onClick={resetColumns} className="w-full text-xs text-amber-600 hover:text-amber-700 mt-2 py-1 font-medium" data-testid="reset-columns-btn">
              Show All Columns
            </button>
          )}
        </PopoverContent>
      </Popover>

      {/* Active Filters Count */}
      {activeFilterCount > 0 && (
        <Button variant="ghost" size="sm" className="h-8 text-xs text-red-600 hover:text-red-700 gap-1" onClick={clearAllFilters} data-testid="clear-all-filters">
          <X className="w-3 h-3" />
          Clear {activeFilterCount} filter{activeFilterCount > 1 ? 's' : ''}
        </Button>
      )}

      {/* Save View Dialog */}
      {saveDialogOpen && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center" onClick={() => setSaveDialogOpen(false)}>
          <div className="bg-white rounded-lg shadow-xl p-4 w-80" onClick={e => e.stopPropagation()}>
            <h3 className="text-sm font-semibold text-slate-800 mb-3">Save Current View</h3>
            <Input 
              value={viewName} 
              onChange={e => setViewName(e.target.value)} 
              placeholder="View name..." 
              className="mb-3"
              data-testid="view-name-input"
              autoFocus
              onKeyDown={e => e.key === 'Enter' && handleSave()}
            />
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="outline" onClick={() => setSaveDialogOpen(false)}>Cancel</Button>
              <Button size="sm" onClick={handleSave} disabled={!viewName.trim()} data-testid="confirm-save-view">Save</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function ColumnFilterRow({ columns, columnFilters, setColumnFilter, isColumnVisible, leadingCells = 0 }) {
  return (
    <tr className="bg-slate-50/80 border-b border-slate-200">
      {leadingCells > 0 && Array.from({ length: leadingCells }).map((_, i) => (
        <td key={`lead-${i}`} className="px-2 py-1.5"><div className="h-7" /></td>
      ))}
      {columns.map(col => {
        if (!isColumnVisible(col.key)) return null;
        if (!col.filterable) {
          return <td key={col.key} className="px-2 py-1.5"><div className="h-7" /></td>;
        }

        const filterValue = columnFilters[col.key] || '';

        if (col.filterType === 'select') {
          return (
            <td key={col.key} className="px-2 py-1.5">
              <select 
                value={filterValue}
                onChange={e => setColumnFilter(col.key, e.target.value)}
                className="w-full h-7 rounded border border-slate-200 bg-white px-1.5 text-xs focus:ring-1 focus:ring-slate-300 focus:outline-none"
                data-testid={`col-filter-${col.key}`}
              >
                <option value="">All</option>
                {(col.filterOptions || []).map(opt => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </td>
          );
        }

        return (
          <td key={col.key} className="px-2 py-1.5">
            <div className="relative">
              <Input 
                value={filterValue}
                onChange={e => setColumnFilter(col.key, e.target.value)}
                placeholder={`Filter...`}
                className="h-7 text-xs pl-2 pr-6"
                data-testid={`col-filter-${col.key}`}
              />
              {filterValue && (
                <button 
                  onClick={() => setColumnFilter(col.key, '')} 
                  className="absolute right-1 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-slate-200"
                >
                  <X className="w-3 h-3 text-slate-400" />
                </button>
              )}
            </div>
          </td>
        );
      })}
    </tr>
  );
}

/**
 * Sortable table header cell for server-side sorting.
 * `field` is the SERVER sort field name; `sortBy`/`sortDir`/`onSort` come from useTableView.
 */
export function SortableHead({ field, label, sortBy, sortDir, onSort, className = '' }) {
  const active = sortBy === field;
  return (
    <TableHead className={`font-mono text-xs uppercase ${className}`}>
      <button
        type="button"
        onClick={() => onSort(field)}
        className={`inline-flex items-center gap-1 hover:text-amber-600 transition-colors ${active ? 'text-amber-600' : ''}`}
        data-testid={`sort-${field}`}
      >
        {label}
        {active
          ? (sortDir === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />)
          : <ChevronsUpDown className="w-3 h-3 opacity-40" />}
      </button>
    </TableHead>
  );
}
